import {Component, effect} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {finalize} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';
import {TranslateModule} from '@ngx-translate/core';
import {AConcreteResource} from '../../../../AConcreteResource';
import {CustomerModel} from '../../../../../models/resources/customer.model';
import {CustomerService} from '../../../../../services/resources/customer.service';
import {HttpErrorsService} from '../../../../../services/utils/http-errors.service';
import {CustomNotificationService} from '../../../../../services/utils/custom-notification.service';
import {SafeUpdateService} from '../../../../../services/utils/safe-update.service';
import {MdbButtonComponent} from '../../../../utils/mdb-button/mdb-button.component';
import {SpinnerComponent} from '../../../../utils/spinner/spinner.component';
import {ApiSelectComponent} from '../../../../utils/api-select/api-select.component';
import {FormInputComponent} from '../../../../utils/form-input/form-input.component';
import {CheckboxInputComponent} from '../../../../utils/checkbox-input/checkbox-input.component';
import {ErpUserService} from '../../../../../services/resources/erp-user.service';
import {PaymentTermService} from '../../../../../services/resources/payment-term.service';
import {PricelistService} from '../../../../../services/resources/pricelist.service';
import {FiscalPositionService} from '../../../../../services/resources/fiscal-position.service';
import {CurrencyService} from '../../../../../services/resources/currency.service';
import {UrlParamModel} from '../../../../../models/url-param.model';

/**
 * Tab "Verkauf & Einkauf" – bildet den gleichnamigen Odoo-Tab am Customer-Model ab.
 */
@Component({
  selector: 'app-customer-sales-purchase',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    MdbButtonComponent,
    SpinnerComponent,
    ApiSelectComponent,
    FormInputComponent,
    CheckboxInputComponent
  ],
  templateUrl: './customer-sales-purchase.component.html',
  styleUrl: './customer-sales-purchase.component.scss',
})
export class CustomerSalesPurchaseComponent extends AConcreteResource<CustomerModel> {
  loading: boolean = false;
  formGroup: FormGroup;

  /** Stabile Referenz, damit api-select nicht bei jeder CD neu lädt. */
  readonly emptyQueryParams: UrlParamModel[] = [];

  constructor(
    router: Router,
    private customerService: CustomerService,
    private httpErrorsService: HttpErrorsService,
    private notificationService: CustomNotificationService,
    private safeUpdateService: SafeUpdateService,
    private formBuilder: FormBuilder,
    public erpUserService: ErpUserService,
    public paymentTermService: PaymentTermService,
    public pricelistService: PricelistService,
    public fiscalPositionService: FiscalPositionService,
    public currencyService: CurrencyService
  ) {
    super(router, customerService);

    this.formGroup = this.formBuilder.group({
      erpUserId: [null],
      erpBuyerId: [null],
      erpPaymentTermId: [null],
      erpSupplierPaymentTermId: [null],
      erpPricelistId: [null],
      erpFiscalPositionId: [null],
      erpPurchaseCurrencyId: [null],
      receiptReminderEmail: [false],
      reminderDateBeforeReceipt: [null, [Validators.min(0)]],
    });

    effect(() => {
      const model = this.model();
      if (model) {
        this.formGroup.patchValue({
          erpUserId: model.erpUserId ?? null,
          erpBuyerId: model.erpBuyerId ?? null,
          erpPaymentTermId: model.erpPaymentTermId ?? null,
          erpSupplierPaymentTermId: model.erpSupplierPaymentTermId ?? null,
          erpPricelistId: model.erpPricelistId ?? null,
          erpFiscalPositionId: model.erpFiscalPositionId ?? null,
          erpPurchaseCurrencyId: model.erpPurchaseCurrencyId ?? null,
          receiptReminderEmail: model.receiptReminderEmail ?? false,
          reminderDateBeforeReceipt: model.reminderDateBeforeReceipt ?? null,
        });
      }
    });
  }

  submit(serverResource: any = null, overrideValues: { [key: string]: any } = {}) {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid) return;

    this.loading = true;

    const model = this.model()!;
    let resource: any = {...this.formGroup.value};

    // Pflichtfelder der Customer-Validierung + Safe-Update
    resource.companyType = model.companyType;
    resource.company = model.company;
    resource.updatedAt = model.updatedAt;

    // Update-Validierung verlangt id/updatedAt der 1:1-Relationen (Safe-Update)
    if (model.person?.id)
      resource.person = {id: model.person.id, updatedAt: model.person.updatedAt};
    if (model.address?.id)
      resource.address = {id: model.address.id, updatedAt: model.address.updatedAt};

    if (serverResource)
      resource = this.safeUpdateService.handleResource(resource, serverResource, overrideValues);

    this.customerService
      .update(model.id, resource)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.notificationService.success('CHANGES_SUCCESSFULLY_SAVED');
        },
        error: (e: HttpErrorResponse) => {
          this.httpErrorsService.handleError(e, (newServerResource, newOverrideValues) => {
            this.submit(newServerResource, newOverrideValues);
          });
        },
      });
  }
}
